import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useBossBabyChatLogStore } from "@/store/useBossBabyChatLogStore";
import type { BossBabyChatSessionDto } from "@/apis/bossBabyChatLog";

/**
 * 필터 타입 정의
 */
type FilterValues = {
  search: string;
  comId: string;
  status: string;
  startDate: string;
  endDate: string;
};

/**
 * API 파라미터 타입 정의
 */
type SessionListParams = {
  page?: number;
  limit?: number;
  comId?: number;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
};

/**
 * BossBabyChatLogSessionList 커스텀 훅
 *
 * @description 보스베이비 채팅 로그 세션 리스트의 비즈니스 로직을 관리하는 훅
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const useBossBabyChatLogSessionList = () => {
  const navigate = useNavigate();
  const {
    sessions,
    loading,
    error,
    pagination,
    fetchSessions,
    clearError,
    reset,
  } = useBossBabyChatLogStore();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [filters, setFilters] = useState<FilterValues>({
    search: "",
    comId: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  /**
   * 필터를 API 파라미터로 변환
   */
  const convertFiltersToParams = useCallback(
    (filterValues: FilterValues): Partial<SessionListParams> => {
      const params: Partial<SessionListParams> = {};

      if (filterValues.search) params.search = filterValues.search;
      if (filterValues.comId) {
        const comIdNum = parseInt(filterValues.comId, 10);
        if (!isNaN(comIdNum)) params.comId = comIdNum;
      }
      if (filterValues.status) params.status = filterValues.status;
      if (filterValues.startDate) params.startDate = filterValues.startDate;
      if (filterValues.endDate) params.endDate = filterValues.endDate;

      return params;
    },
    []
  );

  /**
   * 세션 목록 로드
   */
  const loadSessions = useCallback(async () => {
    const params: SessionListParams = {
      page: pagination.page,
      limit: pagination.limit,
      ...convertFiltersToParams(filters),
    };

    await fetchSessions(params);
  }, [
    pagination.page,
    pagination.limit,
    filters,
    fetchSessions,
    convertFiltersToParams,
  ]);

  // 컴포넌트 마운트 시 Store 상태 초기화 및 데이터 로드
  useEffect(() => {
    reset();
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // reset과 loadSessions는 초기 마운트 시에만 실행

  // 에러 처리
  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarOpen(true);
      clearError();
    }
  }, [error, clearError]);

  /**
   * 페이지 변경 처리
   * @description MUI TablePagination은 0-based 인덱스를 사용하므로 +1 변환 필요
   */
  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      const params = {
        page: newPage + 1, // MUI는 0-based, API는 1-based
        limit: pagination.limit,
        ...convertFiltersToParams(filters),
      };
      fetchSessions(params);
    },
    [fetchSessions, pagination.limit, filters, convertFiltersToParams]
  );

  /**
   * 페이지 크기 변경 처리
   */
  const handleLimitChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newLimit = parseInt(event.target.value, 10);
      const params = {
        page: 1, // 페이지 크기 변경 시 첫 페이지로
        limit: newLimit,
        ...convertFiltersToParams(filters),
      };
      fetchSessions(params);
    },
    [fetchSessions, filters, convertFiltersToParams]
  );

  /**
   * 필터 변경 처리
   */
  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      const newFilters = { ...filters, [key]: value };
      setFilters(newFilters);
      const params = {
        page: 1, // 필터 변경 시 첫 페이지로
        limit: pagination.limit,
        ...convertFiltersToParams(newFilters),
      };
      fetchSessions(params);
    },
    [fetchSessions, pagination.limit, filters, convertFiltersToParams]
  );

  /**
   * 필터 리셋
   */
  const handleResetFilters = useCallback(() => {
    const resetFilters = {
      search: "",
      comId: "",
      status: "",
      startDate: "",
      endDate: "",
    };
    setFilters(resetFilters);
    const params = {
      page: 1,
      limit: pagination.limit,
      ...convertFiltersToParams(resetFilters),
    };
    fetchSessions(params);
  }, [fetchSessions, pagination.limit, convertFiltersToParams]);

  /**
   * 새로고침
   */
  const handleRefresh = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  /**
   * 세션 상세 보기 - 페이지로 이동
   */
  const handleViewSession = useCallback(
    (session: BossBabyChatSessionDto) => {
      navigate(`/boss-baby-chat-log/sessions/${session.sessionUuid}`);
    },
    [navigate]
  );

  /**
   * 스낵바 닫기
   */
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return {
    // 상태
    sessions,
    loading,
    error,
    pagination,
    filters,
    snackbarOpen,
    snackbarMessage,

    // 핸들러
    handlePageChange,
    handleLimitChange,
    handleFilterChange,
    handleResetFilters,
    handleRefresh,
    handleViewSession,
    handleSnackbarClose,
  };
};
